var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _dec10, _dec11, _dec12, _dec13, _dec14, _dec15, _dec16, _dec17, _dec18, _dec19, _dec20, _dec21, _dec22, _dec23, _dec24, _dec25, _dec26, _dec27, _dec28, _dec29, _dec30, _dec31, _class, _class2, _descriptor, _descriptor2, _descriptor3;

function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }

function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'proposal-class-properties is enabled and runs after the decorators transform.'); }

function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }

/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { Component, Event, html, Prop, Watch } from '@sick-davinci/ce-decorators';
import { style } from '../_style/Common.style';
import * as QuickChart from '@sick-davinci/quickchart';
import { ColoredDataElement } from '../_utils/ColoredDataElement.js'; // should be imported first, so css variables from theme-base are available

import { CssTools } from '../_utils/CssTools.js';
import { isColor, resolveCssColor, toCssColorValue } from '../_utils/ColorTools.js';
import { ArrayTools } from '../_utils/ArrayTools.js';
import { ValueAxisBuilder } from '../_utils/ValueAxisBuilder.js';
import { TimeAxisBuilder } from '../_utils/TimeAxisBuilder.js';
import { ThemeService } from '../_utils/ThemeService.js';
import { createValueFormat } from '../_utils/ValueFormat.js';
import { defaultShorthandLabels } from '../_utils/NumberFormatter.js';
import { createTicksFormat } from '../_utils/TicksFormat.js';
/**
 * A high-performance time-based line chart (similar to an oszilloscope).
 * Handle fast data input with various data-series, each having an own y-axis.
 * Users can navigate back and forth in time as well as zoom into important time-frames.
 * All data is passed through the `addSample` function instead of a property because data is managed by this element itself in a high performance manner.
 *
 * Plotter scaling can be changed using mouse-scroll + CTRL/SHIFT keys.
 *
 * Please have look at {@link expectedSamplingPeriod} which is crucial for the behavior and performance of this chart.
 *
 * @example
 * ```
 * <div style="width: 500px; height: 300px">
 *   <davinci-plotter id="plotter" visible-value-axes="[0, 1, 2]" stop data-series-configs='[
                   { "valueRange": { "start": 0, "end": 10 }, "lineColor": 0 },
                   { "valueRange": { "start": -10, "end": 10 }, "lineColor": 1 },
                   { "valueRange": { "start": 0, "end": 50 }, "lineColor": 2 }
                 ]'></davinci-plotter>
 * </div>
 * <script>
 *  const plotter = document.getElementById('plotter');
 *  plotter.addSamples(
 *    {timestamp: Date.now()-2000, values:[0,0,0]},
 *    {timestamp: Date.now()-1000, values:[5, 50,500]},
 *    {timestamp: Date.now(), values:[10,100,1000]}
 * );
 * </script>
 * ```
 */

export let Plotter = (_dec = Component({
  tag: 'davinci-plotter',
  style
}), _dec2 = Prop(), _dec3 = Reflect.metadata("design:type", Object), _dec4 = Prop(), _dec5 = Reflect.metadata("design:type", Number), _dec6 = Prop(), _dec7 = Reflect.metadata("design:type", Number), _dec8 = Prop(), _dec9 = Reflect.metadata("design:type", String), _dec10 = Prop(), _dec11 = Reflect.metadata("design:type", Number), _dec12 = Prop(), _dec13 = Reflect.metadata("design:type", Object), _dec14 = Prop({
  type: String
}), _dec15 = Reflect.metadata("design:type", Object), _dec16 = Prop(), _dec17 = Reflect.metadata("design:type", Object), _dec18 = Prop(), _dec19 = Reflect.metadata("design:type", Object), _dec20 = Event(), _dec21 = Reflect.metadata("design:type", Object), _dec22 = Prop(), _dec23 = Reflect.metadata("design:type", Object), _dec24 = Prop(), _dec25 = Reflect.metadata("design:type", Number), _dec26 = Prop(), _dec27 = Reflect.metadata("design:type", Boolean), _dec28 = Watch('palette'), _dec29 = Reflect.metadata("design:paramtypes", []), _dec30 = Prop(), _dec31 = Reflect.metadata("design:type", Boolean), _dec(_class = (_class2 = class Plotter extends ColoredDataElement {
  constructor(...args) {
    super(...args);
    this._dataSeries = [];
    this._userDefinedDataSeriesConfigs = [];
    this._valueAxisMaxTickCount = 20;
    this._valueAxisWidth = ValueAxisBuilder.AXIS_DEFAULT_WIDTH;
    this._timeAxisTitle = '';
    this._historyLength = 3600;

    _initializerDefineProperty(this, "legendLabels", _descriptor, this);

    this._expectedSamplingPeriod = 'EveryHundredMillis';
    this._validateSelectedExpectedSamplingPeriod = true;
    this._visibleLines = [0, 1, 2, 3, 4, 5];
    this._visibleValueAxes = [0];

    _initializerDefineProperty(this, "visibleTimeRangeChanged", _descriptor2, this);

    this._validateSelectedVisibleTimeRange = true;
    this._selectedVisibleTimeRange = null;
    this._crossWirePosition = 0.9;
    this._play = true;

    _initializerDefineProperty(this, "interactiveLegend", _descriptor3, this);

    this._samples = [];

    this._themeListener = () => {
      this._updateStyle();
    };
  }

  /**
   * The configurations used for the data series of this plotter
   */
  get dataSeriesConfigs() {
    return this._userDefinedDataSeriesConfigs;
  }

  set dataSeriesConfigs(newDataSeriesConfigs) {
    this._userDefinedDataSeriesConfigs = ArrayTools.REPLACE_PROPERTIES(this._userDefinedDataSeriesConfigs, newDataSeriesConfigs);

    this._updateStyle();
  }
  /**
   * The maximum number of ticks to be used for any value axis.
   *
   * The number of visible ticks depends on
   * - the currently visible value range,
   * - the height of the value axis and
   * - the font used for the tick values (tick values should not overlap).
   *
   * So it might be that there are less ticks visible than the number specified by this property, but there are never more ticks visible.
   *
   * Set this property to a large value (e. g. 100) to ensure that as many ticks as possible are visible.
   *
   * @default 20
   */


  get valueAxisMaxTickCount() {
    return this._valueAxisMaxTickCount;
  }

  set valueAxisMaxTickCount(value) {
    this._valueAxisMaxTickCount = value;

    this._updateStyle();
  }

  /**
   * The width of the value-axis (in rem)
   *
   * @default ValueAxisBuilder.AXIS_DEFAULT_WIDTH
   */
  get valueAxisWidth() {
    return this._valueAxisWidth;
  }

  set valueAxisWidth(value) {
    this._valueAxisWidth = ValueAxisBuilder.SANITIZE_AXIS_WIDTH(value);

    this._updateStyle();
  }

  /**
   * The title of the time axis.
   *
   * Blank strings will be treated as if no title is set.
   *
   * @default empty string
   */
  get timeAxisTitle() {
    return this._timeAxisTitle;
  }

  set timeAxisTitle(value) {
    this._timeAxisTitle = value;

    this._updateStyle();
  }

  // default is one hour = 3600 seconds

  /**
   * Guaranteed duration in seconds the history stores samples for.
   * The plotter guarantees to store samples for at least this duration (in seconds).
   *
   * @default 3600
   */
  get historyLength() {
    return this._historyLength;
  }

  set historyLength(value) {
    this._historyLength = value;

    this._updateData();
  }
  /**
   * List of all legend-labels. if not set, no legend is displayed.
   */


  /**
   * The expected time between two consecutive samples.
   *
   * The history is optimized for certain sampling periods.
   * The value given here helps to adopt the history to your needs.
   * To ensure the best performance pick a sampling period close to the one of your data source.
   * If in doubt, select a sampling period that is smaller than that of your data source.
   * E. g. if you want to add samples every 50 ms to the history choose the sampling period "EveryTenMillis".
   *
   * If you add significantly more samples to the history than expected, those samples will not be lost.
   * However, the history will consume more memory than intended and down-sampling will take more time than it could.
   *
   * If you add significantly less samples to the history than expected, there will not be considerable performance impacts,
   * but you do not use the history to its full potential.
   *
   * Changing the value of this property will also clear the history of the plotter.
   *
   * @default 'EveryHundredMillis'
   */
  get expectedSamplingPeriod() {
    return this._expectedSamplingPeriod;
  }

  set expectedSamplingPeriod(samplingPeriod) {
    const newExpectedSamplingPeriod = this._sanitizeSamplingPeriod(samplingPeriod, 'EveryHundredMillis');

    if (this._expectedSamplingPeriod !== newExpectedSamplingPeriod) {
      this._expectedSamplingPeriod = newExpectedSamplingPeriod;
      this._validateSelectedExpectedSamplingPeriod = true;
      this.clearHistory();

      this._updateData();
    }
  }

  /**
   * The indices of the lines to show. All others are hidden.
   *
   * The default is that the first 6 lines are visible.
   *
   * @default [0, 1, 2, 3, 4, 5]
   */
  get visibleLines() {
    return this._visibleLines;
  }

  set visibleLines(indices) {
    this._visibleLines = indices;

    this._updateStyle();
  }

  /**
   * The indices of the value axes to show. All others are hidden.
   *
   * The default is that the value axis of the first line is visible.
   *
   * @default [0]
   */
  get visibleValueAxes() {
    return this._visibleValueAxes;
  }

  set visibleValueAxes(indices) {
    this._visibleValueAxes = indices;

    this._updateStyle();
  }
  /**
   * Emitted if the visible time range has changed
   */


  /**
   * The time range that is currently visible
   *
   * @default null
   */
  get visibleTimeRange() {
    // If the api is ready return the current visible time range.
    if (this._api) {
      return this._api.getVisibleTimeRange();
    } // If the api is not ready yet return the time range selected by the user.


    if (this._selectedVisibleTimeRange) {
      return this._selectedVisibleTimeRange;
    } // If the api is not ready yet and no time range has been selected by the user return an empty time range.


    return {
      start: 0.0,
      end: 0.0
    };
  }

  set visibleTimeRange(timeRange) {
    if (timeRange.start >= timeRange.end) {
      // eslint-disable-next-line
      console.warn(`The selected time range is invalid. The start (${new Date(timeRange.start).toISOString()}) must lie before the end (${new Date(timeRange.end).toISOString()}).
The visible time range will remain unchanged.`); // do not use invalid time range but bail out

      return;
    }

    this._selectedVisibleTimeRange = timeRange;

    this._updateStyle();
  }

  // default is 90% of this element's width

  /**
   * The position of the cross wire along the x-axis relative to the width of the plotter.
   *
   * 0 denotes the left edge and 1 denotes the right edge of the plotter.
   *
   * Example: the cross-wire position is 0.7 and the with of the plotter is 1000px wide.
   * Then the location of the cross wire is 700px from the left edge of the plotter and
   * 300px from the right edge of the plotter.
   *
   * @default 0.9
   */
  get crossWirePosition() {
    return this._crossWirePosition;
  }

  set crossWirePosition(value) {
    this._crossWirePosition = value;

    this._updateStyle();
  } // start in play mode


  /**
   * Stops the automatic feeding of the plotter canvas.
   *
   * Playing also translates the plotter to 'now'.
   *
   * @default false
   */
  get stop() {
    return !this._play;
  }

  set stop(stop) {
    this._play = !stop;

    this._updateData();
  }
  /**
   * Update style if palette attribute changes
   */


  paletteWatcher() {
    this._updateStyle();
  }
  /**
   * If enabled, the legend items can be used to toggle data-series
   */


  /**
   * Adds samples to the history
   * All samples must be in chronological order
   *
   * @param samples - the samples to be added
   */
  addSamples(...samples) {
    samples.forEach(sample => {
      // handle wrong parameter type (e. g. passed from IE11 which does not support spread operator)
      if (Array.isArray(sample)) {
        for (let i = 0; i < sample.length; i++) {
          // [DAVIDE-400] use a for-loop here to prevent the call stack size from exceeding
          this._samples.push(sample[i]);
        }
      } else {
        this._samples.push(sample);
      }
    });

    this._updateData();
  }
  /**
   * Removes all samples previously added to the history
   */


  clearHistory() {
    this._samples = [];

    if (this._api) {
      this._api.clearHistory();
    }
  }
  /**
   * Resets zoom and translation
   */


  resetNavigation() {
    if (this._api) {
      this._api.resetView();
    }
  }
  /**
   * Retrieves the current zoom
   *
   * @returns the current zoom.
   */


  getZoom() {
    if (this._api) {
      return this._api.getZoom();
    }

    return {
      scaleX: 1.0,
      scaleY: 1.0
    };
  }
  /**
   * Modifies the current Zoom.
   *
   * @param zoom - the new Zoom
   * @param zoomCenterX - a value in [0..1] that is the x-coordinate of the center of the zoom relative to this element's width (from left to right)
   * @param zoomCenterY - a value in [0..1] that is the y-coordinate of the center of the zoom relative to this element's height (from top to bottom)
   */


  modifyZoom(zoom, zoomCenterX, zoomCenterY) {
    if (this._api) {
      this._api.modifyZoom(zoom, zoomCenterX, zoomCenterY);
    }
  }

  _init() {
    var _this$shadowRoot;

    if (!this._api) {
      return;
    }

    const holder = (_this$shadowRoot = this.shadowRoot) === null || _this$shadowRoot === void 0 ? void 0 : _this$shadowRoot.getElementById('holder');

    if (!holder) {
      return;
    }

    holder.appendChild(this._api.holder); // initial update of the data

    this._updateData(); // initial update of the style


    this._updateStyle();
  }
  /**
   * See base class
   */


  componentFirstRender() {
    this._init();

    const holder = this.shadowRoot.getElementById('holder');
    holder.addEventListener('VisibleTimeRangeChanged', evt => {
      this.visibleTimeRangeChanged.emit(evt.detail);
    }, {
      passive: true
    });
  }
  /**
   * See base class
   */


  render() {
    var _this$shadowRoot2, _this$legendLabels;

    // recycle holder is needed: holds the actual chart as a child
    let holder = (_this$shadowRoot2 = this.shadowRoot) === null || _this$shadowRoot2 === void 0 ? void 0 : _this$shadowRoot2.getElementById('holder');

    if (!holder) {
      holder = document.createElement('div');
      holder.setAttribute('id', 'holder');
    }

    if (((_this$legendLabels = this.legendLabels) === null || _this$legendLabels === void 0 ? void 0 : _this$legendLabels.length) > 0) {
      const colors = this.legendLabels.map((_ll, i) => {
        var _this$_userDefinedDat, _this$_userDefinedDat2;

        return (_this$_userDefinedDat = (_this$_userDefinedDat2 = this._userDefinedDataSeriesConfigs[i]) === null || _this$_userDefinedDat2 === void 0 ? void 0 : _this$_userDefinedDat2.lineColor) !== null && _this$_userDefinedDat !== void 0 ? _this$_userDefinedDat : i;
      });
      return html`
      <davinci-legend
        placement="N"
        small-font
        compact
        .interactiveLegend=${this.interactiveLegend}
        symbols='["line"]'
        symbol-size="${CssTools.REM2PIXEL(1.5)}"
        toggle-size="${CssTools.REM2PIXEL(.875)}"
        .labels=${this.legendLabels}
        .palette=${this.palette}
        .colors=${colors}
        @legendStateChanged=${this._handleLegendStateChanged}>
      ${holder}
      </davinci-legend>
      `;
    } else {
      return html`${holder}`;
    }
  }

  componentConnected() {
    super.componentConnected();
    ThemeService.GET_INSTANCE().addListener(this._themeListener);
    this._api = QuickChart.createTimeLineChart(); // this could be a reattach without a following call for componentFirstRender -> reestablish QuickChart API

    this._init();
  }

  componentDisconnected() {
    super.componentDisconnected();
    ThemeService.GET_INSTANCE().removeListener(this._themeListener);
    this._dataSeries = []; // We have been detached from the DOM -> clean up

    if (this._api) {
      var _this$shadowRoot3, _this$shadowRoot3$get;

      (_this$shadowRoot3 = this.shadowRoot) === null || _this$shadowRoot3 === void 0 ? void 0 : (_this$shadowRoot3$get = _this$shadowRoot3.getElementById('holder')) === null || _this$shadowRoot3$get === void 0 ? void 0 : _this$shadowRoot3$get.removeChild(this._api.holder);

      this._api.dispose();
    }

    delete this._api;
  }
  /**
   * Checks if the {@link expectedSamplingPeriod} fits the samples to be added
   */


  _validateExpectedSamplingPeriod() {
    if (!this._validateSelectedExpectedSamplingPeriod) {
      return;
    }

    const numberOfSamples = this._samples.length;

    if (numberOfSamples <= 1) {
      // No validation necessary.
      return;
    }

    this._validateSelectedExpectedSamplingPeriod = false;
    let averageSamplingPeriodMillis = 0.0;

    for (let i = 1; i < numberOfSamples; i++) {
      averageSamplingPeriodMillis += (this._samples[i].timestamp - this._samples[i - 1].timestamp) / numberOfSamples;
    }

    averageSamplingPeriodMillis = Math.round(averageSamplingPeriodMillis);

    const expectedSamplingPeriodMillis = this._toMillis(this.expectedSamplingPeriod);

    if (averageSamplingPeriodMillis / 20.0 > expectedSamplingPeriodMillis || averageSamplingPeriodMillis * 20.0 < expectedSamplingPeriodMillis) {
      // eslint-disable-next-line
      console.warn(`The selected expected sampling period is "${this.expectedSamplingPeriod}" which equals ${expectedSamplingPeriodMillis.toLocaleString()} milliseconds between two consecutive samples.
The average sampling period of the added samples is ${averageSamplingPeriodMillis.toLocaleString()} milliseconds.
Please ADJUST THE EXPECTED SAMPLING PERIOD to better fit the added samples.`);
    }
  }
  /**
   * Updates the data of the plotter
   *
   * Call this function every time a data-related property changes
   */


  _updateData() {
    if (this._api === undefined) {
      return;
    }

    this._validateExpectedSamplingPeriod();

    if (this._samples.length) {
      this._sanitizeSamples();

      this._updateDataSeriesConfigurations();

      try {
        this._api.addSamples(this._samples);
      } finally {
        // Ensure that the samples are cleared - even if an exception is thrown
        this._samples = [];
      }
    }

    const timeLineChartData = {
      historySettings: {
        expectedSamplingPeriod: this._expectedSamplingPeriod,
        minGapSizeFactor: 10.0,
        // default value
        guaranteedHistoryLength: this._historyLength
      },
      play: this._play
    };

    this._api.setData(timeLineChartData);
  }

  _sanitizeSamples() {
    if (this._samples.length === 0) {
      return;
    }

    const lastSample = this._samples[this._samples.length - 1];
    const seriesCount = lastSample.values.length; // check that every sample has the same number of data series

    const indexInvalidSample = this._samples.findIndex(sample => sample.values.length !== seriesCount);

    if (indexInvalidSample !== -1) {
      const invalidSample = this._samples[indexInvalidSample]; // eslint-disable-next-line no-console

      console.warn(`The latest sample contains values for ${seriesCount} data series but the sample at index ${indexInvalidSample} contains values for ${invalidSample.values.length} data series.\n
    Only the latest sample will be added to the history.`);
      this._samples = [lastSample];
    }
  }

  _updateDataSeriesConfigurations() {
    if (this._samples.length === 0) {
      // The data-series configuration is computed from the samples. No samples means we cannot update the configuration.
      return;
    } // this._samples must be sanitized at this point


    const lastSample = this._samples[this._samples.length - 1];
    const newDataSeriesCount = lastSample.values.length;
    const oldDataSeriesCount = this._dataSeries.length;

    if (newDataSeriesCount === oldDataSeriesCount) {
      // no update necessary
      return;
    }

    this._dataSeries = this._computeDataSeriesFromSample(lastSample);

    this._api.setDataSeries(this._dataSeries);
  }

  _computeDataSeriesFromSample(sample) {
    return sample.values.map((_, index) => {
      // store the sample-values with a precision of 4 fraction digits
      return {
        dataStructureId: index,
        fractionDigits: 4
      };
    });
  }

  _validateVisibleTimeRange() {
    if (!this._validateSelectedVisibleTimeRange) {
      return;
    }

    if (this._selectedVisibleTimeRange === null) {
      // No validation necessary.
      return;
    } // Check once whether the visible time range selected by the user matches the expectedSamplingPeriod


    this._validateSelectedVisibleTimeRange = false;
    const visibleSelectedDurationMillis = this._selectedVisibleTimeRange.end - this._selectedVisibleTimeRange.start;

    const expectedSamplingPeriodMillis = this._toMillis(this.expectedSamplingPeriod);

    const expectedVisibleDurationMinMillis = expectedSamplingPeriodMillis * 30; // at least 30 samples visible

    const expectedVisibleDurationMaxMillis = expectedSamplingPeriodMillis * 2000; // at most 2000 samples visible

    if (visibleSelectedDurationMillis < expectedVisibleDurationMinMillis || visibleSelectedDurationMillis > expectedVisibleDurationMaxMillis) {
      const formattedVisibleSelectedTimeRangeStart = new Date(this._selectedVisibleTimeRange.start).toISOString();
      const formattedVisibleSelectedTimeRangeEnd = new Date(this._selectedVisibleTimeRange.end).toISOString(); // eslint-disable-next-line

      console.warn(`The selected visible time range is [${formattedVisibleSelectedTimeRangeStart} - ${formattedVisibleSelectedTimeRangeEnd}] which equals ${visibleSelectedDurationMillis.toLocaleString()} milliseconds.
The expected sampling period is ${expectedSamplingPeriodMillis.toLocaleString()} milliseconds.
Assuming that two samples are at least 3 pixels apart this implies that the plotter is ${(visibleSelectedDurationMillis * 3 / expectedSamplingPeriodMillis).toLocaleString()} pixels wide.
Please ADJUST THE VISIBLE TIME RANGE to better fit the expected sampling period.`);
    }
  }
  /**
   * Updates the style of the plotter
   *
   * Call this function every time a style-related property changes
   */


  _updateStyle() {
    var _this$shadowRoot4, _this$_userDefinedDat3;

    if (this._api === undefined) {
      return;
    }

    const host = (_this$shadowRoot4 = this.shadowRoot) === null || _this$shadowRoot4 === void 0 ? void 0 : _this$shadowRoot4.host;

    if (!host) {
      return;
    }

    const fontFamily = CssTools.GET_COMPUTED_FONT_FAMILY(host);

    this._validateVisibleTimeRange();

    const dataSeriesConfigurations = this._computeDataSeriesConfigurations();

    const lineUnits = this.dataSeriesConfigs.map(dataSeriesConfig => dataSeriesConfig.unit || '');
    const lineFormat = this.dataSeriesConfigs.map(dataSeriesConfig => {
      var _dataSeriesConfig$sho;

      const shorthandLabels = (_dataSeriesConfig$sho = dataSeriesConfig.shorthandLabels) !== null && _dataSeriesConfig$sho !== void 0 ? _dataSeriesConfig$sho : defaultShorthandLabels;
      const decimalPlaces = dataSeriesConfig.decimalPlaces;
      return createValueFormat(shorthandLabels, decimalPlaces);
    });
    const timeLineChartStyle = {
      visibleTimeRange: this._selectedVisibleTimeRange,
      crossWirePosition: this._crossWirePosition,
      dataSeriesConfigurations: dataSeriesConfigurations,
      visibleLines: this._visibleLines,
      visibleValueAxes: this._visibleValueAxes,
      valueAxesGap: CssTools.REM2PIXEL(1.0),
      valueAxesMaxCount: 8,
      valueAxesBackground: resolveCssColor('var(--davinci-color-background-elements-overlay)'),
      crossWireFont: {
        family: fontFamily,
        weight: 600,
        size: CssTools.REM2PIXEL(0.75),
        style: 'normal'
      },
      crossWireFormat: {
        format(index, value, locale) {
          var _lineUnits, _lineFormat, _lineFormat2;

          const lineUnit = (_lineUnits = lineUnits[index % lineUnits.length]) !== null && _lineUnits !== void 0 ? _lineUnits : '';
          const formattedValue = (_lineFormat = (_lineFormat2 = lineFormat[index % lineFormat.length]) === null || _lineFormat2 === void 0 ? void 0 : _lineFormat2.call(lineFormat, value, locale)) !== null && _lineFormat !== void 0 ? _lineFormat : value.toFixed(2);
          return lineUnit ? `${formattedValue} ${lineUnit}` : formattedValue;
        }

      }
    }; // reset the user settings

    this._selectedVisibleTimeRange = null;
    const timeAxisBuilder = new TimeAxisBuilder();
    timeAxisBuilder.fontFamily = fontFamily;
    timeAxisBuilder.title = this.timeAxisTitle;
    timeLineChartStyle.timeAxisStyle = timeAxisBuilder.build(); // If one value axis has a title all value axes should behave as if they had a title.
    // This ensures that all value axes have the same width.

    const valueAxesHaveTitles = ((_this$_userDefinedDat3 = this._userDefinedDataSeriesConfigs) === null || _this$_userDefinedDat3 === void 0 ? void 0 : _this$_userDefinedDat3.findIndex(dataSeriesConfig => {
      var _dataSeriesConfig$uni, _dataSeriesConfig$uni2;

      return ((_dataSeriesConfig$uni = dataSeriesConfig.unit) === null || _dataSeriesConfig$uni === void 0 ? void 0 : (_dataSeriesConfig$uni2 = _dataSeriesConfig$uni.trim()) === null || _dataSeriesConfig$uni2 === void 0 ? void 0 : _dataSeriesConfig$uni2.length) > 0;
    })) !== -1; // Beware that we cannot use the default tick length and tick-label gap for
    // the plotter because the plotter is the only chart whose value axes have
    // visible tick marks.

    const valueAxisBuilder = new ValueAxisBuilder();
    valueAxisBuilder.fontFamily = fontFamily;
    valueAxisBuilder.horizontal = false;
    valueAxisBuilder.tickLength = 0.5; // set explicitly and do not use default value

    valueAxisBuilder.tickLabelGap = 0.5; // set explicitly and do not use default value

    valueAxisBuilder.title = valueAxesHaveTitles ? 'X' : '';
    valueAxisBuilder.maxTickCount = this.valueAxisMaxTickCount;
    valueAxisBuilder.axisWidth = this.valueAxisWidth;
    valueAxisBuilder.shorthandLabels = defaultShorthandLabels; // will be ignored by quickchart

    valueAxisBuilder.decimalPlaces = undefined; // will be ignored by quickchart

    timeLineChartStyle.valueAxesStyle = valueAxisBuilder.build(); // The following values are automatically computed if set to null

    timeLineChartStyle.valueAxesStyle.title = null;
    timeLineChartStyle.valueAxesStyle.axisLineColor = null;
    timeLineChartStyle.valueAxesStyle.tickLabelColor = null;

    this._api.setStyle(timeLineChartStyle);
  }

  _computeDataSeriesConfigurations() {
    const result = [];

    for (let lineIndex = 0; lineIndex < Math.max(this.palette.length, this.dataSeriesConfigs.length); lineIndex++) {
      var _userDefinedDataSerie, _userDefinedDataSerie2;

      const dataSeriesConfiguration = {};
      result.push(dataSeriesConfiguration);
      const userDefinedDataSeriesConfig = this._userDefinedDataSeriesConfigs[lineIndex] || {};
      const lineColor = (_userDefinedDataSerie = userDefinedDataSeriesConfig.lineColor) !== null && _userDefinedDataSerie !== void 0 ? _userDefinedDataSerie : lineIndex;
      dataSeriesConfiguration.label = userDefinedDataSeriesConfig.unit;
      dataSeriesConfiguration.valueRange = userDefinedDataSeriesConfig.valueRange;
      const shorthandLabels = (_userDefinedDataSerie2 = userDefinedDataSeriesConfig.shorthandLabels) !== null && _userDefinedDataSerie2 !== void 0 ? _userDefinedDataSerie2 : defaultShorthandLabels;
      const decimalPlaces = userDefinedDataSeriesConfig.decimalPlaces;
      const ticksFormat = createTicksFormat(shorthandLabels, decimalPlaces);
      dataSeriesConfiguration.ticksFormat = {
        format(ticksIn, ticksOut, locale) {
          ticksFormat(ticksIn, ticksOut, locale);
        }

      };
      dataSeriesConfiguration.lineStyle = {
        color: isColor(lineColor) ? toCssColorValue(lineColor, this.palette) : null
      };
    }

    return result;
  }

  _isSamplingPeriod(samplingPeriod) {
    switch (samplingPeriod) {
      case 'EveryMillisecond':
      case 'EveryTenMillis':
      case 'EveryHundredMillis':
      case 'EverySecond':
      case 'EveryTenSeconds':
      case 'EveryMinute':
      case 'EveryTenMinutes':
      case 'EveryHour':
      case 'Every6Hours':
      case 'Every24Hours':
        return true;

      default:
        return false;
    }
  }

  _sanitizeSamplingPeriod(samplingPeriod, fallback = 'EveryHundredMillis') {
    if (this._isSamplingPeriod(samplingPeriod)) {
      return samplingPeriod;
    }

    if (!this._isSamplingPeriod(fallback)) {
      throw new Error(`The provided fallback "${fallback}" is not of the correct type`);
    } // eslint-disable-next-line


    console.warn(`"${samplingPeriod}" is not a valid sampling period; "${fallback}" will be used instead.`);
    return fallback;
  }

  _toMillis(samplingPeriod) {
    switch (samplingPeriod) {
      case 'EveryMillisecond':
        return 1;

      case 'EveryTenMillis':
        return 10;

      case 'EveryHundredMillis':
        return 100;

      case 'EverySecond':
        return 1000;

      case 'EveryTenSeconds':
        return 10 * 1000;

      case 'EveryMinute':
        return 60 * 1000;

      case 'EveryTenMinutes':
        return 10 * 60 * 1000;

      case 'EveryHour':
        return 60 * 60 * 1000;

      case 'Every6Hours':
        return 6 * 60 * 60 * 1000;

      case 'Every24Hours':
        return 24 * 60 * 60 * 1000;

      default:
        throw new Error(`unsupported sampling period <${samplingPeriod}>`);
    }
  }

  _handleLegendStateChanged(e) {
    this._legendActiveState = e.detail;
    this.visibleLines = this._legendActiveState.map((ls, i) => ls.active ? i : -1).filter(i => i >= 0);
    this.visibleValueAxes = this.visibleLines;
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "dataSeriesConfigs", [_dec2, _dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "dataSeriesConfigs"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "valueAxisMaxTickCount", [_dec4, _dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "valueAxisMaxTickCount"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "valueAxisWidth", [_dec6, _dec7], Object.getOwnPropertyDescriptor(_class2.prototype, "valueAxisWidth"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "timeAxisTitle", [_dec8, _dec9], Object.getOwnPropertyDescriptor(_class2.prototype, "timeAxisTitle"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "historyLength", [_dec10, _dec11], Object.getOwnPropertyDescriptor(_class2.prototype, "historyLength"), _class2.prototype), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "legendLabels", [_dec12, _dec13], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _applyDecoratedDescriptor(_class2.prototype, "expectedSamplingPeriod", [_dec14, _dec15], Object.getOwnPropertyDescriptor(_class2.prototype, "expectedSamplingPeriod"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "visibleLines", [_dec16, _dec17], Object.getOwnPropertyDescriptor(_class2.prototype, "visibleLines"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "visibleValueAxes", [_dec18, _dec19], Object.getOwnPropertyDescriptor(_class2.prototype, "visibleValueAxes"), _class2.prototype), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "visibleTimeRangeChanged", [_dec20, _dec21], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "visibleTimeRange", [_dec22, _dec23], Object.getOwnPropertyDescriptor(_class2.prototype, "visibleTimeRange"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "crossWirePosition", [_dec24, _dec25], Object.getOwnPropertyDescriptor(_class2.prototype, "crossWirePosition"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "stop", [_dec26, _dec27], Object.getOwnPropertyDescriptor(_class2.prototype, "stop"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "paletteWatcher", [_dec28, _dec29], Object.getOwnPropertyDescriptor(_class2.prototype, "paletteWatcher"), _class2.prototype), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "interactiveLegend", [_dec30, _dec31], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
})), _class2)) || _class);
/**
 * The config of a data series in the Plotter
 */
